import { useEffect, useMemo, useRef, useState } from "react";
import { Activity, Radio, RefreshCw, Search, ShieldCheck, PhoneCall, Building2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const AUTO_REFRESH_MS = 2000;

function qualityLabel(quality) {
  const state = String(quality?.state || "idle").toLowerCase();
  if (state === "excellent") return { label: "Excelente", className: "tag-online" };
  if (state === "good") return { label: "Boa", className: "tag-online" };
  if (state === "fair") return { label: "Regular", className: "tag-warn" };
  if (state === "poor") return { label: "Ruim", className: "tag-offline" };
  if (state === "measuring") return { label: "Medindo", className: "" };
  if (state === "waiting") return { label: "Aguardando", className: "" };
  return { label: "Sem chamada", className: "" };
}

function callStateLabel(state) {
  const normalized = String(state || "idle").toLowerCase();
  if (normalized === "ringing") return { label: "Tocando", className: "tag-warn" };
  if (normalized === "dialing") return { label: "Chamando", className: "tag-warn" };
  if (normalized === "active") return { label: "Em chamada", className: "tag-online" };
  if (normalized === "hold") return { label: "Em espera", className: "tag-warn" };
  return { label: "Livre", className: "" };
}

export default function Registrations() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "superadmin";
  const [data, setData] = useState({ extensions: [], trunks: [], failures: [], summary: {}, updated_at: null });
  const [tab, setTab] = useState("extensions");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [branchFilter, setBranchFilter] = useState("all");
  const [lastLoadedAt, setLastLoadedAt] = useState(null);
  const [serverUpdatedAt, setServerUpdatedAt] = useState(null);
  const [nextRefreshIn, setNextRefreshIn] = useState(Math.ceil(AUTO_REFRESH_MS / 1000));
  const [livePulse, setLivePulse] = useState(false);
  const refreshTimerRef = useRef(null);
  const countdownTimerRef = useRef(null);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    if (silent) setRefreshing(true);
    try {
      const response = await api.get("/registrations");
      const payload = response.data || { extensions: [], trunks: [], failures: [], summary: {}, updated_at: null };
      setData(payload);
      setLastLoadedAt(new Date());
      setServerUpdatedAt(payload.updated_at ? new Date(payload.updated_at) : null);
      setNextRefreshIn(Math.ceil(AUTO_REFRESH_MS / 1000));
      setLivePulse(true);
      setError("");
    } catch (e) {
      setError(e.response?.data?.detail || "Falha ao consultar registros.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    const refresh = () => {
      if (document.visibilityState === "visible") {
        load(true);
      }
    };
    refreshTimerRef.current = setInterval(refresh, AUTO_REFRESH_MS);
    countdownTimerRef.current = setInterval(() => {
      setNextRefreshIn((current) => (current <= 1 ? Math.ceil(AUTO_REFRESH_MS / 1000) : current - 1));
    }, 1000);
    const pulseTimer = setInterval(() => {
      setLivePulse(false);
    }, 650);
    const visibilityHandler = () => {
      if (document.visibilityState === "visible") {
        load(true);
      }
    };
    document.addEventListener("visibilitychange", visibilityHandler);
    return () => {
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
      if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
      clearInterval(pulseTimer);
      document.removeEventListener("visibilitychange", visibilityHandler);
    };
  }, []);

  const s = data.summary || {};

  const branchOptions = useMemo(() => {
    const grouped = new Map();
    for (const row of data.extensions || []) {
      const key = String(row.branch_id || "__principal__");
      if (!grouped.has(key)) {
        grouped.set(key, {
          value: key,
          label: row.branch_name || "Principal",
        });
      }
    }
    return [{ value: "all", label: "Todas as filiais" }, ...Array.from(grouped.values()).sort((a, b) => a.label.localeCompare(b.label, "pt-BR"))];
  }, [data.extensions]);

  const filteredExtensions = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (data.extensions || []).filter((row) => {
      const haystack = [
        row.tenant_name,
        row.branch_name,
        row.numero,
        row.nome,
        row.network_ip,
        row.contact_ip,
        row.user_agent,
        row.domain,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      const searchMatch = !needle || haystack.includes(needle);
      const statusMatch =
        statusFilter === "all" ||
        (statusFilter === "online" && row.online) ||
        (statusFilter === "offline" && !row.online) ||
        (statusFilter === "ringing" && row.call_state === "ringing") ||
        (statusFilter === "active" && ["active", "hold"].includes(row.call_state));
      const currentBranchKey = String(row.branch_id || "__principal__");
      const branchMatch = branchFilter === "all" || branchFilter === currentBranchKey;
      return searchMatch && statusMatch && branchMatch;
    });
  }, [branchFilter, data.extensions, search, statusFilter]);

  const filteredTrunks = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (data.trunks || []).filter((row) => {
      const haystack = [row.tenant_name, row.nome, row.host, row.porta, row.status, row.registration_status]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      const searchMatch = !needle || haystack.includes(needle);
      const statusMatch =
        statusFilter === "all" ||
        (statusFilter === "online" && row.status === "online") ||
        (statusFilter === "offline" && row.status !== "online");
      return searchMatch && statusMatch;
    });
  }, [data.trunks, search, statusFilter]);

  const filteredFailures = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (data.failures || []).filter((row) => {
      const haystack = [row.tenant_name, row.extension, row.domain, row.ip, row.reason, row.raw]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      const searchMatch = !needle || haystack.includes(needle);
      const statusMatch = statusFilter === "all" || statusFilter === "offline";
      return searchMatch && statusMatch;
    });
  }, [data.failures, search, statusFilter]);

  const cards = [
    {
      label: "Ramais registrados",
      value: s.extensions_online || 0,
      sub: `${s.extensions_total || 0} cadastrados`,
      icon: ShieldCheck,
      tone: "text-[color:var(--accent-green)]",
    },
    {
      label: "Ramais tocando",
      value: s.extensions_ringing || 0,
      sub: "Recebendo chamada",
      icon: PhoneCall,
      tone: "text-[color:var(--accent-amber)]",
    },
    {
      label: "Ramais em chamada",
      value: s.extensions_in_call || 0,
      sub: "Chamada ativa ou em espera",
      icon: Activity,
      tone: "text-[color:var(--accent-blue)]",
    },
    {
      label: "Troncos online",
      value: s.trunks_online || 0,
      sub: `${s.trunks_total || 0} monitorados`,
      icon: Radio,
      tone: "text-[color:var(--accent-green)]",
    },
    {
      label: "Falhas recentes de login",
      value: s.registration_failures_recent || 0,
      sub: "Ultimas 48 horas",
      icon: ShieldCheck,
      tone: "text-[color:var(--accent-red)]",
    },
  ];

  return (
    <div className="space-y-5" data-testid="registrations-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">
            Configuracao do sistema
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">Monitoramento de registros</h1>
          <p className="text-sm text-[color:var(--text-secondary)] mt-1">
            Acompanhe as tentativas de login SIP dos ramais, veja o motivo de falha no registro e acompanhe o status dos troncos em tempo real.
          </p>
          <div className="flex flex-wrap items-center gap-2 mt-3 text-[11px] font-mono">
            <span className={`sbc-tag ${livePulse ? "tag-online" : ""}`}>tempo real</span>
            <span className="text-[color:var(--text-muted)]">
              proxima leitura em {nextRefreshIn}s
            </span>
            {serverUpdatedAt && (
              <span className="text-[color:var(--text-muted)]">
                servidor: {serverUpdatedAt.toLocaleString("pt-BR")}
              </span>
            )}
          </div>
        </div>
        <button className="sbc-btn" onClick={() => load()} disabled={loading} title="Atualizar">
          <RefreshCw size={14} className={loading || refreshing ? "animate-spin" : ""} />
          Atualizar
        </button>
      </div>

      {error && (
        <div className="text-xs font-mono text-[color:var(--accent-red)] border border-[rgba(239,68,68,0.3)] bg-[rgba(239,68,68,0.08)] px-3 py-2 rounded-sm">
          ! {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
        {cards.map((card) => (
          <div key={card.label} className="sbc-card p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[10px] uppercase text-[color:var(--text-muted)]">{card.label}</div>
                <div className="text-2xl font-mono mt-1">{card.value}</div>
                <div className="text-[11px] text-[color:var(--text-muted)] mt-1">{card.sub}</div>
              </div>
              <div className="w-8 h-8 flex items-center justify-center border border-[color:var(--border-default)] rounded-sm bg-[color:var(--bg-elevated)]">
                <card.icon size={16} className={card.tone} />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="sbc-card p-4 space-y-3">
        <div className="flex flex-wrap gap-2 items-center justify-between">
          <div className="inline-flex border border-[color:var(--border-default)] p-1 gap-1">
            <button className={`sbc-btn ${tab === "extensions" ? "sbc-btn-primary" : ""}`} onClick={() => setTab("extensions")}>
              <Activity size={14} />
              Ramais
            </button>
            <button className={`sbc-btn ${tab === "trunks" ? "sbc-btn-primary" : ""}`} onClick={() => setTab("trunks")}>
              <Radio size={14} />
              Troncos
            </button>
            <button className={`sbc-btn ${tab === "failures" ? "sbc-btn-primary" : ""}`} onClick={() => setTab("failures")}>
              <ShieldCheck size={14} />
              Falhas de login
            </button>
          </div>

          <div className="flex flex-wrap gap-2 items-center">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--text-muted)]" />
              <input
                className="sbc-input pl-9 min-w-[240px]"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={
                  tab === "extensions"
                    ? "Buscar ramal, nome, IP, filial..."
                    : tab === "trunks"
                      ? "Buscar tronco, cliente, host..."
                      : "Buscar falha, ramal, dominio, IP..."
                }
              />
            </div>
            {tab === "extensions" && (
              <select
                className="sbc-input min-w-[200px]"
                value={branchFilter}
                onChange={(e) => setBranchFilter(e.target.value)}
              >
                {branchOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            )}
            <select className="sbc-input min-w-[180px]" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">Todos os status</option>
              <option value="online">Online</option>
              <option value="offline">Offline</option>
              {tab === "extensions" && <option value="ringing">Tocando</option>}
              {tab === "extensions" && <option value="active">Em chamada</option>}
            </select>
          </div>
        </div>

        {tab === "extensions" ? (
          <div className="overflow-auto">
            <table className="sbc-table">
              <thead>
                <tr>
                  {isSuperAdmin && <th>Cliente</th>}
                  <th>Filial</th>
                  <th>Ramal</th>
                  <th>Login SIP</th>
                  <th>IP / Porta</th>
                  <th>Transporte</th>
                  <th>Aparelho</th>
                  <th>Chamada</th>
                  <th>Qualidade</th>
                  <th>Expira</th>
                  <th>Registro</th>
                </tr>
              </thead>
              <tbody>
                {filteredExtensions.length === 0 ? (
                  <tr>
                    <td colSpan={isSuperAdmin ? 11 : 10} className="text-center text-[color:var(--text-muted)] py-8">
                      Nenhum ramal encontrado.
                    </td>
                  </tr>
                ) : (
                  filteredExtensions.map((row) => {
                    const callState = callStateLabel(row.call_state);
                    const quality = qualityLabel(row.quality);
                    return (
                      <tr key={row.id}>
                        {isSuperAdmin && <td>{row.tenant_name || "-"}</td>}
                        <td>{row.branch_name || "Principal"}</td>
                        <td>
                          <div className="font-mono font-medium">{row.numero}</div>
                          <div className="text-[10px] text-[color:var(--text-muted)]">{row.nome || "Sem nome"}</div>
                        </td>
                        <td className="font-mono text-xs">{row.domain || "-"}</td>
                        <td className="font-mono text-xs">
                          <div>{row.network_ip || row.contact_ip || "-"}</div>
                          <div className="text-[10px] text-[color:var(--text-muted)]">
                            porta {row.network_port || row.contact_port || "-"}
                          </div>
                        </td>
                        <td className="font-mono text-xs">{row.transport || "-"}</td>
                        <td className="text-xs max-w-[240px] truncate" title={row.user_agent || ""}>
                          {row.user_agent || "-"}
                        </td>
                        <td>
                          <span className={`sbc-tag ${callState.className}`}>{callState.label}</span>
                        </td>
                        <td>
                          <span className={`sbc-tag ${quality.className}`}>{quality.label}</span>
                        </td>
                        <td className="font-mono text-xs">{row.online && row.expires != null ? `${row.expires}s` : "-"}</td>
                        <td>
                          {row.online ? (
                            <span className="sbc-tag tag-online">REGISTRADO{row.contacts > 1 ? ` · ${row.contacts}` : ""}</span>
                          ) : (
                            <span className="sbc-tag tag-offline">OFFLINE</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        ) : tab === "trunks" ? (
          <div className="overflow-auto">
            <table className="sbc-table">
              <thead>
                <tr>
                  {isSuperAdmin && <th>Cliente</th>}
                  <th>Tronco</th>
                  <th>Destino</th>
                  <th>Conectividade</th>
                  <th>Login</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredTrunks.length === 0 ? (
                  <tr>
                    <td colSpan={isSuperAdmin ? 6 : 5} className="text-center text-[color:var(--text-muted)] py-8">
                      Nenhum tronco encontrado.
                    </td>
                  </tr>
                ) : (
                  filteredTrunks.map((row) => (
                    <tr key={row.id}>
                      {isSuperAdmin && <td>{row.tenant_name || "-"}</td>}
                      <td className="font-medium">{row.nome}</td>
                      <td className="font-mono text-xs">{row.host}:{row.porta}</td>
                      <td className="font-mono text-xs">{row.connectivity_status || "-"}</td>
                      <td>
                        {row.registration_required ? (
                          <span className={`sbc-tag ${row.registration_status === "registered" ? "tag-online" : "tag-offline"}`}>
                            {row.registration_status}
                          </span>
                        ) : (
                          <span className="text-[color:var(--text-muted)]">não exigido</span>
                        )}
                      </td>
                      <td>
                        {row.status === "online" ? (
                          <span className="sbc-tag tag-online">online</span>
                        ) : (
                          <span className="sbc-tag tag-offline">offline</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="overflow-auto">
            <table className="sbc-table">
              <thead>
                <tr>
                  <th>Horario</th>
                  {isSuperAdmin && <th>Cliente</th>}
                  <th>Ramal / Login</th>
                  <th>Dominio</th>
                  <th>IP origem</th>
                  <th>Motivo</th>
                  <th>Log</th>
                </tr>
              </thead>
              <tbody>
                {filteredFailures.length === 0 ? (
                  <tr>
                    <td colSpan={isSuperAdmin ? 7 : 6} className="text-center text-[color:var(--text-muted)] py-8">
                      Nenhuma falha recente encontrada.
                    </td>
                  </tr>
                ) : (
                  filteredFailures.map((row, index) => (
                    <tr key={`${row.logged_at}-${row.extension}-${index}`}>
                      <td className="font-mono text-xs">{row.logged_at || "-"}</td>
                      {isSuperAdmin && <td>{row.tenant_name || "-"}</td>}
                      <td className="font-mono text-xs">{row.extension || "-"}</td>
                      <td className="font-mono text-xs">{row.domain || "-"}</td>
                      <td className="font-mono text-xs">{row.ip || "-"}</td>
                      <td>
                        <span className="sbc-tag tag-offline">{row.reason || "Falha de autenticacao"}</span>
                      </td>
                      <td className="text-xs max-w-[460px] truncate" title={row.raw || ""}>
                        {row.raw || "-"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="sbc-card p-4 flex flex-wrap items-center gap-3 text-xs text-[color:var(--text-secondary)]">
        <Building2 size={14} />
        <span>
          Este painel facilita o suporte de registro SIP, mostrando quem registrou, quem falhou e o motivo do erro de login do ramal.
        </span>
      </div>

      <div className="text-[10px] font-mono text-[color:var(--text-muted)]">
        Atualizado no navegador em {lastLoadedAt ? lastLoadedAt.toLocaleString("pt-BR") : "-"} · leitura automatica a cada 2s
      </div>
    </div>
  );
}
